(() => {
  if (window.RareTCGVariantPickerInitialize) {
    window.RareTCGVariantPickerInitialize();
    return;
  }

  const ROOT_SELECTOR = '.rtcg-variant-picker';
  const SELECT_SELECTOR = '.rtcg-variant-select__control';
  const INITIALIZED = 'data-rtcg-custom-dropdown-ready';
  const instances = new WeakMap();

  const parseOptionText = (rawText) => {
    const text = String(rawText || '').replace(/\s+/g, ' ').trim();
    const priceMatch = text.match(/(?:—|-)?\s*([£€$]\s?\d[\d.,]*)\s*$/);
    const unavailable = /unavailable|sold\s*out/i.test(text);

    if (!priceMatch) {
      return { label: text, price: '', unavailable };
    }

    const label = text
      .slice(0, priceMatch.index)
      .replace(/[—-]\s*$/, '')
      .trim();

    return {
      label: label || text,
      price: priceMatch[1],
      unavailable
    };
  };

  class RareTCGVariantDropdown {
    constructor(root) {
      this.root = root;
      this.select = root.querySelector(SELECT_SELECTOR);
      this.wrapper = root.querySelector('.rtcg-variant-select');

      if (!this.select || !this.wrapper || root.hasAttribute(INITIALIZED)) {
        return;
      }

      try {
        this.build();
        this.bind();
        this.syncFromSelect();

        this.root.setAttribute(INITIALIZED, 'true');
        this.root.classList.add('rtcg-variant-picker--custom');
        this.select.classList.add('rtcg-variant-select__control--native-hidden');
        instances.set(this.root, this);
      } catch (error) {
        this.button?.remove();
        this.menu?.remove();
        this.root.removeAttribute(INITIALIZED);
        this.root.classList.remove('rtcg-variant-picker--custom');
        this.select.classList.remove('rtcg-variant-select__control--native-hidden');
        console.error('[RareTCG] Variant picker enhancement failed; using the native select.', error);
      }
    }

    build() {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.className = 'rtcg-variant-dropdown__trigger';
      this.button.setAttribute('aria-haspopup', 'listbox');
      this.button.setAttribute('aria-expanded', 'false');

      this.button.innerHTML = `
        <span class="rtcg-variant-dropdown__trigger-content">
          <span class="rtcg-variant-dropdown__trigger-label"></span>
          <span class="rtcg-variant-dropdown__trigger-price"></span>
        </span>
        <span
          class="rtcg-variant-dropdown__trigger-caret"
          aria-hidden="true"
        ></span>
      `;

      this.menu = document.createElement('div');
      this.menu.className = 'rtcg-variant-dropdown__menu';
      this.menu.setAttribute('role', 'listbox');
      this.menu.setAttribute('tabindex', '-1');
      this.menu.hidden = true;

      this.wrapper.appendChild(this.button);
      this.wrapper.appendChild(this.menu);

      this.renderOptions();
    }

    renderOptions() {
      this.menu.innerHTML = '';

      [...this.select.options].forEach((option, index) => {
        const parsed = parseOptionText(option.textContent);
        const available =
          option.dataset.rtcgAvailable !== 'false' &&
          !parsed.unavailable;

        const item = document.createElement('button');

        item.type = 'button';
        item.className = 'rtcg-variant-dropdown__option';
        item.dataset.value = option.value;
        item.dataset.index = String(index);
        item.setAttribute('role', 'option');
        item.setAttribute(
          'aria-selected',
          option.selected ? 'true' : 'false'
        );

        if (!available) {
          item.classList.add('is-unavailable');
        }

        item.innerHTML = `
          <span class="rtcg-variant-dropdown__option-main">
            <span
              class="rtcg-variant-dropdown__option-marker"
              aria-hidden="true"
            ></span>

            <span class="rtcg-variant-dropdown__option-label"></span>
          </span>

          <span class="rtcg-variant-dropdown__option-meta">
            <span class="rtcg-variant-dropdown__option-price"></span>

            ${
              !available
                ? '<span class="rtcg-variant-dropdown__option-status">Unavailable</span>'
                : ''
            }
          </span>
        `;

        item.querySelector(
          '.rtcg-variant-dropdown__option-label'
        ).textContent = parsed.label;

        item.querySelector(
          '.rtcg-variant-dropdown__option-price'
        ).textContent = parsed.price;

        this.menu.appendChild(item);
      });
    }

    bind() {
      this.button.addEventListener('click', (event) => {
        const wasOpen = !this.menu.hidden;
        this.toggle();
        // Pointer closing should not leave the buy box in a focused state.
        if (wasOpen && event.detail > 0) this.button.blur();
      });

      this.menu.addEventListener('click', (event) => {
        const optionButton = event.target.closest(
          '.rtcg-variant-dropdown__option'
        );

        if (!optionButton) return;

        this.selectValue(optionButton.dataset.value, event.detail === 0);
      });

      this.button.addEventListener('keydown', (event) => {
        this.onTriggerKeydown(event);
      });

      this.menu.addEventListener('keydown', (event) => {
        this.onMenuKeydown(event);
      });

      this.select.addEventListener('change', () => {
        this.syncFromSelect();
      });

    }

    selectValue(value, keyboardActivated = false) {
      if (this.select.value !== value) {
        this.select.value = value;

        this.select.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      }

      this.syncFromSelect();
      this.close();
      // Restore keyboard focus, but remove the persistent touch/mouse halo.
      if (keyboardActivated) {
        this.button.focus({ preventScroll: true });
      } else {
        this.button.blur();
      }
    }

    syncFromSelect() {
      const selected =
        this.select.options[this.select.selectedIndex] ||
        this.select.options[0];

      if (!selected) return;

      const parsed = parseOptionText(selected.textContent);

      this.button.querySelector(
        '.rtcg-variant-dropdown__trigger-label'
      ).textContent = parsed.label;

      this.button.querySelector(
        '.rtcg-variant-dropdown__trigger-price'
      ).textContent = parsed.price;

      this.menu
        .querySelectorAll('.rtcg-variant-dropdown__option')
        .forEach((item) => {
          item.setAttribute(
            'aria-selected',
            item.dataset.value === selected.value ? 'true' : 'false'
          );
        });
    }

    open(focusOption = false) {
      if (!this.menu.hidden) return;

      this.menu.hidden = false;
      this.root.classList.add('is-open');
      this.button.setAttribute('aria-expanded', 'true');

      const selected =
        this.menu.querySelector('[aria-selected="true"]') ||
        this.menu.querySelector('.rtcg-variant-dropdown__option');

      // On touch/mouse, leave the page position stable and allow direct swiping.
      // On keyboard, focus an option so arrow navigation scrolls it into view.
      if (focusOption) selected?.focus();
    }

    close() {
      if (this.menu.hidden) return;

      // An option can retain DOM focus after the listbox is hidden.
      // Release it so :focus-within does not leave the closed picker glowing.
      if (this.menu.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      this.menu.hidden = true;
      this.root.classList.remove('is-open');
      this.button.setAttribute('aria-expanded', 'false');
    }

    toggle() {
      this.menu.hidden ? this.open() : this.close();
    }

    onTriggerKeydown(event) {
      if (
        ['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)
      ) {
        event.preventDefault();
        this.open(true);
      }
    }

    onMenuKeydown(event) {
      const items = [
        ...this.menu.querySelectorAll(
          '.rtcg-variant-dropdown__option'
        )
      ];

      const index = items.indexOf(document.activeElement);

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        this.button.focus();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        document.activeElement?.click();
        return;
      }

      if (
        !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)
      ) {
        return;
      }

      event.preventDefault();

      let nextIndex = index;

      if (event.key === 'ArrowDown') {
        nextIndex = Math.min(index + 1, items.length - 1);
      }

      if (event.key === 'ArrowUp') {
        nextIndex = Math.max(index - 1, 0);
      }

      if (event.key === 'Home') {
        nextIndex = 0;
      }

      if (event.key === 'End') {
        nextIndex = items.length - 1;
      }

      items[nextIndex]?.focus();
    }
  }

  const initialize = (scope = document) => {
    const roots = [];

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      roots.push(root);
    });

    roots.forEach((root) => {
      if (!root.hasAttribute(INITIALIZED)) {
        new RareTCGVariantDropdown(root);
      }
    });
  };

  window.RareTCGVariantPickerInitialize = initialize;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
    });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
  });

  document.addEventListener('product-info:loaded', (event) => {
    initialize(event.target);
  });

  document.addEventListener('click', (event) => {
    document.querySelectorAll(`${ROOT_SELECTOR}.is-open`).forEach((root) => {
      if (!root.contains(event.target)) {
        instances.get(root)?.close();
      }
    });
  });

  if (
    typeof subscribe === 'function' &&
    typeof PUB_SUB_EVENTS !== 'undefined' &&
    PUB_SUB_EVENTS.variantChange
  ) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data } = {}) => {
      const productInfo = data?.sectionId
        ? document.querySelector(`product-info[data-section="${CSS.escape(String(data.sectionId))}"]`)
        : null;

      initialize(productInfo || document);
    });
  }
})();
